import { toast } from "react-toastify";
import { t } from "i18next";
import {
  deletePreference,
  fetchPreference,
  fetchPreferences,
  isStaleWriteError,
  savePreference,
} from "./userPreferencesApi";
import { trackEvent } from "../../utils/analytics/trackEvent";

/**
 * SPEC-0061 §4.3 — the ONE client of the server-side preference store.
 *
 * Documents are addressed by (scope, prefKey); the user is always the token's.
 * `load(scopes)` fetches every document of the scopes in one call and keeps
 * them in memory; `get` is synchronous from then on. `set` updates memory at
 * once and PUTs 800 ms later (one timer per document, If-Match = the known
 * version); `flush` sends everything pending (tab hidden, logout).
 *
 * Failure policy (FBK-03): a 409 takes the server version, re-applies the keys
 * this client changed and retries once; a network error or 5xx keeps the change
 * in memory and retries with backoff; after 3 failures one toast says the layout
 * will be retried. A 4xx other than 409 is a bug on our side — logged, dropped.
 * A write to a scope whose load failed first reloads it: a one-key document
 * must never replace the server's.
 */
export const SAVE_DEBOUNCE_MS = 800;
const RETRY_BASE_MS = 2000;
const FAILURES_BEFORE_TOAST = 3;

const keyOf = (scope, prefKey) => `${scope}\u0000${prefKey}`;

const own = new Map(); // keyOf → { scope, prefKey, state, version }
const published = new Map(); // keyOf → { scope, prefKey, state, version, updatedBy }
const loaded = new Set();
const failed = new Set(); // scopes whose last load attempt failed (defaults apply, reloadable)
const loading = new Map(); // scope → Promise
const pending = new Map(); // keyOf → { timer (debounce), retryTimer (backoff), inFlight, dirty, dirtyKeys, failures, retried409 }
const listeners = new Set();
let portfolioScopeLoaded = null;
let retryToastShown = false;
let generation = 0; // bumped by clear(): a load or a write started before it is discarded
let ready = false; // the session bootstrap is over (load, import, replay): the shell may mount

const notify = (event) => {
  listeners.forEach((listener) => {
    try {
      listener(event);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("[user-preferences] listener failed", e);
    }
  });
};

const isValuesDoc = (state) => Boolean(state) && typeof state.values === "object" && state.values !== null;

/** The keys of a family document that differ between two states (null: not a family document). */
const changedKeys = (previous, next) => {
  if (!isValuesDoc(next)) return null;
  const before = isValuesDoc(previous) ? previous.values : {};
  const keys = new Set();
  Object.keys(next.values).forEach((k) => {
    if (!(k in before) || before[k] !== next.values[k]) keys.add(k);
  });
  Object.keys(before).forEach((k) => {
    if (!(k in next.values)) keys.add(k);
  });
  return keys;
};

const store = (dto) => {
  const key = keyOf(dto.scope, dto.prefKey);
  if (!dto.published && pending.has(key) && own.has(key)) {
    // A local change made before the load landed wins; the server version
    // becomes its If-Match. For a family document the other keys come from
    // the server — the local write only knew the key it changed.
    const local = own.get(key);
    local.version = dto.version;
    if (isValuesDoc(local.state) && isValuesDoc(dto.state)) {
      local.state = { ...dto.state, ...local.state, values: { ...dto.state.values, ...local.state.values } };
    }
    return;
  }
  const entry = {
    scope: dto.scope,
    prefKey: dto.prefKey,
    state: dto.state,
    version: dto.version,
    updatedBy: dto.updatedBy,
  };
  (dto.published ? published : own).set(key, entry);
};

const rememberPortfolioScope = (scopes) => {
  const portfolio = scopes.find((s) => s.startsWith("portfolio:"));
  if (portfolio) portfolioScopeLoaded = portfolio;
};

/**
 * Fetches the documents of the scopes not loaded yet (one call). Never throws:
 * a failed load leaves the scopes reloadable and the app on its defaults.
 */
const load = (scopes) => {
  const wanted = [...new Set(scopes)].filter((s) => s && !loaded.has(s));
  rememberPortfolioScope(scopes);
  if (!wanted.length) return Promise.resolve();
  const fresh = wanted.filter((s) => !loading.has(s));
  const gen = generation;
  const request = fresh.length
    ? Promise.resolve(fetchPreferences(fresh))
        .then((docs) => {
          if (gen !== generation) return; // the session ended meanwhile: not ours any more
          (Array.isArray(docs) ? docs : []).forEach(store);
          fresh.forEach((s) => {
            loaded.add(s);
            failed.delete(s);
          });
          notify({ type: "load", scopes: fresh });
        })
        .catch((error) => {
          if (gen !== generation) return;
          fresh.forEach((s) => failed.add(s));
          // eslint-disable-next-line no-console
          console.warn("[user-preferences] load failed; defaults apply until the next load", error?.message);
          notify({ type: "load-failed", scopes: fresh });
        })
        .finally(() => {
          if (gen === generation) fresh.forEach((s) => loading.delete(s));
        })
    : Promise.resolve();
  fresh.forEach((s) => loading.set(s, request));
  return Promise.all(wanted.map((s) => loading.get(s) || request)).then(() => undefined);
};

const get = (scope, prefKey, fallback = null) => {
  const entry = own.get(keyOf(scope, prefKey));
  return entry ? entry.state : fallback;
};

const getPublished = (scope, prefKey, fallback = null) => {
  const entry = published.get(keyOf(scope, prefKey));
  return entry ? entry.state : fallback;
};

const versionOf = (scope, prefKey) => own.get(keyOf(scope, prefKey))?.version;
const has = (scope, prefKey) => own.has(keyOf(scope, prefKey));
const isLoaded = (scope) => loaded.has(scope);
/** The scope's load attempt is over — loaded, or failed with the defaults in force. */
const isSettled = (scope) => loaded.has(scope) || failed.has(scope);
/** The bootstrap is over: documents loaded (or failed), the one-time import done, the store replayed. */
const isReady = () => ready;
const currentPortfolioScope = () => portfolioScopeLoaded;

const retryDelay = (failures) => RETRY_BASE_MS * 2 ** (failures - 1);

class ScopeUnavailable extends Error {}

const send = (key) => {
  const job = pending.get(key);
  const entry = own.get(key);
  if (!job || !entry) {
    pending.delete(key);
    return Promise.resolve();
  }
  clearTimeout(job.timer);
  clearTimeout(job.retryTimer);
  job.timer = null;
  job.retryTimer = null;
  if (job.inFlight) {
    job.dirty = true;
    return job.inFlight;
  }
  job.dirty = false;
  const sentKeys = job.dirtyKeys;
  job.dirtyKeys = null;
  const gen = generation;
  // The PUT goes out synchronously (a flush on logout counts on it); the one
  // exception is a scope that is not loaded (still loading past the shell's
  // deadline, or whose load failed): it is loaded first, so the server document
  // merges under the local change (store) and the PUT carries the whole document
  // with its If-Match, never an unconditional replace of the family.
  const write = () => {
    const current = own.get(key);
    if (!current) return Promise.resolve();
    if (!loaded.has(current.scope)) throw new ScopeUnavailable(`${current.scope} not loaded`);
    return Promise.resolve(savePreference(current.scope, current.prefKey, current.state, current.version)).then(
      (dto) => {
        const latest = own.get(key);
        if (latest) latest.version = dto?.version ?? latest.version;
        job.failures = 0;
        job.retried409 = false;
        retryToastShown = false;
      }
    );
  };
  const attempt = loaded.has(entry.scope)
    ? write()
    : load([entry.scope]).then(() => (gen === generation ? write() : undefined));
  job.inFlight = attempt
    .catch((error) => (gen === generation ? onSaveError(key, job, error, sentKeys) : undefined))
    .finally(() => {
      job.inFlight = null;
      // A reset() in the meantime dropped this job: a newer job under the same key is not ours to end.
      if (pending.get(key) !== job || gen !== generation) return;
      if (job.dirty) {
        job.dirty = false;
        send(key);
      } else if (!job.timer && !job.retryTimer) {
        pending.delete(key);
      }
    });
  return job.inFlight;
};

/**
 * 409: the server document with OUR changed keys on top (the other keys are the other tab's).
 * With no changed key of our own (an echo write), the server document is simply taken.
 */
const mergeStale = (entry, serverState, keys) => {
  if (!isValuesDoc(serverState) || !isValuesDoc(entry.state)) return;
  if (!keys.size) {
    entry.state = serverState;
    return;
  }
  const values = { ...serverState.values };
  keys.forEach((k) => {
    if (k in entry.state.values) values[k] = entry.state.values[k];
    else delete values[k];
  });
  entry.state = { ...serverState, ...entry.state, values };
};

const backOff = (key, job) => {
  job.failures += 1;
  if (job.failures >= FAILURES_BEFORE_TOAST && !retryToastShown) {
    retryToastShown = true;
    const fallback = "Your layout could not be saved; it will be retried";
    toast.warn(t("preferences.saveRetry", { defaultValue: fallback }) || fallback);
  }
  job.retryTimer = setTimeout(() => send(key), retryDelay(job.failures));
};

const onSaveError = (key, job, error, sentKeys) => {
  const entry = own.get(key);
  if (!entry) return undefined;
  if (isStaleWriteError(error)) {
    const sent = new Set(sentKeys || []);
    // Another tab wrote meanwhile: take its version and its other keys, keep OUR
    // changed keys (a set() made during the re-fetch included).
    let keys = sent;
    const merged = Promise.resolve(fetchPreference(entry.scope, entry.prefKey))
      .then((dto) => {
        keys = new Set([...sent, ...(job.dirtyKeys || [])]);
        if (dto?.version == null) return;
        entry.version = dto.version;
        mergeStale(entry, dto.state, keys);
      })
      .catch(() => {})
      .then(() => {
        job.dirtyKeys = new Set([...keys, ...(job.dirtyKeys || [])]);
      });
    if (!job.retried409) {
      // The first conflict is retried at once.
      job.retried409 = true;
      return merged.then(() => {
        job.dirty = true;
      });
    }
    // A second conflict in a row: another tab keeps writing. Merged again, retried after a backoff.
    return merged.then(() => {
      job.dirty = false;
      backOff(key, job);
    });
  }
  const status = error?.response?.status;
  if (status && status >= 400 && status < 500) {
    // eslint-disable-next-line no-console
    console.error(`[user-preferences] ${entry.scope}/${entry.prefKey} rejected (${status}); change dropped`);
    return undefined;
  }
  job.dirtyKeys = new Set([...(sentKeys || []), ...(job.dirtyKeys || [])]);
  backOff(key, job);
  return undefined;
};

const schedule = (key, delay) => {
  const job = pending.get(key) || {
    timer: null,
    retryTimer: null,
    inFlight: null,
    dirty: false,
    dirtyKeys: null,
    failures: 0,
    retried409: false,
  };
  pending.set(key, job);
  if (job.inFlight) {
    job.dirty = true;
    return job;
  }
  // A new change after a failed write is sent on the debounce, not after the backoff.
  clearTimeout(job.timer);
  clearTimeout(job.retryTimer);
  job.retryTimer = null;
  job.timer = setTimeout(() => send(key), delay);
  return job;
};

// Key order matters to the bounded families (the most recent key is last).
const sameKeyOrder = (a, b) =>
  !isValuesDoc(a) || !isValuesDoc(b) || Object.keys(a.values).join("\u0000") === Object.keys(b.values).join("\u0000");

/** In memory at once; PUT debounced per document. A family write that changes no key (a mount-time echo) is not sent. */
const set = (scope, prefKey, state) => {
  const key = keyOf(scope, prefKey);
  const entry = own.get(key);
  const changed = changedKeys(entry?.state, state);
  if (entry && changed && changed.size === 0 && sameKeyOrder(entry.state, state) && !pending.has(key)) return;
  if (entry) entry.state = state;
  else own.set(key, { scope, prefKey, state, version: undefined });
  notify({ type: "change", scope, prefKey, state });
  const job = schedule(key, SAVE_DEBOUNCE_MS);
  if (changed && job.dirtyKeys !== undefined) {
    job.dirtyKeys = job.dirtyKeys || new Set();
    changed.forEach((k) => job.dirtyKeys.add(k));
  }
  if (!changed) job.dirtyKeys = null; // whole-document write
};

/** Forgets the document at once and DELETEs it (reset to the published version / default). */
const reset = (scope, prefKey) => {
  const key = keyOf(scope, prefKey);
  const job = pending.get(key);
  let afterInFlight = Promise.resolve();
  if (job) {
    clearTimeout(job.timer);
    clearTimeout(job.retryTimer);
    pending.delete(key);
    // A PUT on the wire would otherwise land after the DELETE and resurrect the copy.
    if (job.inFlight) afterInFlight = job.inFlight.catch(() => {});
  }
  const existed = own.delete(key);
  notify({ type: "reset", scope, prefKey });
  // A scope that is not loaded may hold the document on the server: DELETE anyway.
  if (!existed && loaded.has(scope)) return Promise.resolve();
  return afterInFlight
    .then(() => {
      // A set() made while the PUT was on the wire brought the document back: its own PUT replaces the row.
      if (own.has(key)) return undefined;
      trackEvent("preferences.reset", { scope: String(scope).split(":")[0], prefKey: String(prefKey).split(":")[0] });
      return deletePreference(scope, prefKey);
    })
    .catch((error) => {
      // eslint-disable-next-line no-console
      console.warn(`[user-preferences] reset of ${scope}/${prefKey} failed`, error?.message);
    });
};

/** Stores a document written outside the debounce path (the one-time import). */
const adopt = (dto) => {
  store(dto);
  notify({ type: "change", scope: dto.scope, prefKey: dto.prefKey, state: dto.state });
};

/** Sends a document's pending change and waits for the write on the wire to land, then for a re-send it caused. */
const settle = (key, retried = false) => {
  const job = pending.get(key);
  if (!job) return Promise.resolve();
  if (job.inFlight) return job.inFlight.then(() => settle(key, retried));
  if (job.dirty || job.timer) return send(key).then(() => settle(key, retried));
  // A failed write waiting in its backoff gets one immediate attempt (logout, tenant switch and
  // the language swap all rely on flush); a second failure keeps the backoff.
  if (job.retryTimer && !retried) return send(key).then(() => settle(key, true));
  return Promise.resolve();
};

const flush = () => Promise.all([...pending.keys()].map(settle)).then(() => undefined);

const subscribe = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

/** Forgets everything (logout / user switch). Pending writes must be flushed before. */
const clear = () => {
  generation += 1;
  ready = false;
  pending.forEach((job) => {
    clearTimeout(job.timer);
    clearTimeout(job.retryTimer);
  });
  pending.clear();
  own.clear();
  published.clear();
  loaded.clear();
  failed.clear();
  loading.clear();
  portfolioScopeLoaded = null;
  retryToastShown = false;
  notify({ type: "clear" });
};

/** The session bootstrap calls it once the load, the one-time import and the store replay are done. */
const markReady = () => {
  if (ready) return;
  ready = true;
  notify({ type: "ready" });
};

const pendingCount = () => pending.size;

/** Test seam: marks scopes as loaded (and the bootstrap as done) without a fetch; seed documents with adopt. */
const markLoadedForTests = (scopes) => {
  scopes.forEach((s) => loaded.add(s));
  rememberPortfolioScope(scopes);
  ready = true;
};

if (typeof document !== "undefined" && document.addEventListener) {
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flush();
  });
}
if (typeof window !== "undefined" && window.addEventListener) {
  // A scope whose load failed offline is retried once the network is back.
  window.addEventListener("online", () => {
    if (failed.size) load([...failed]);
  });
}

const userPreferences = {
  load,
  get,
  getPublished,
  has,
  versionOf,
  set,
  reset,
  adopt,
  flush,
  subscribe,
  clear,
  markReady,
  isLoaded,
  isSettled,
  isReady,
  currentPortfolioScope,
  pendingCount,
  markLoadedForTests,
};

export default userPreferences;
